import React, {
  useContext,
  useMemo,
  useEffect,
  useCallback,
  useReducer,
  useRef,
  useState,
} from 'react';

import EditorContext, { EditorContextProps } from '../Context';
import reducer, {
  actions,
  initialState,
} from '~/App/Modules/Testimonials/Context/QuestionsReducer';
import { confirmAsync } from '~/App/Components/Modals/Confirm';
import {
  RichText,
  getRichTextFromSlateData,
  parseRichTextToSlateData,
} from '@vimily/bonjoro-ui-components-v2/RichTextEditor';
import { Questionnaire } from '../../../Components/Questionnaire';
import { QuestionnaireInputType } from '@vimily/bonjoro-ui-components-v2/Questionnaire';
import { QuestionItem } from '../../../Components/Questionnaire/Question';
import { sideBarContentsId } from '../../../Components/Editor/Sidebar';

export const MAX_QUESTIONS = 6;

export type QuestionsProps = {
  onFocus?: (index: number) => void;
};

export default function Questions({ onFocus }: QuestionsProps) {
  const [scrollToEnd, setScrollToEnd] = useState(false);
  const containerRef = useRef(document.getElementById(sideBarContentsId));
  const context: EditorContextProps = useContext(EditorContext);
  const { testimonial, update } = context;
  const mappedQuestions = useMemo(
    () =>
      testimonial.questions?.map((question) => ({
        ...question,
        value: getRichTextFromSlateData(question.question_rich),
        color: question.bg_color,
        responseTypes: question.response_types,
      })),
    [testimonial.questions]
  );

  const [state, dispatch] = useReducer(
    reducer,
    mappedQuestions ?? initialState
  );

  const handleOnAdd = useCallback(() => {
    dispatch(actions.addQuestion());

    // Scroll preview to the newly added Question
    onFocus?.(state.length);
    setScrollToEnd(true);
  }, [state]);

  // Scrolls to the end of the Questions Sidebar
  useEffect(() => {
    if (scrollToEnd && containerRef?.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
      setScrollToEnd(false);
    }
  }, [scrollToEnd, containerRef]);

  const handleOnUpdate = (item: QuestionItem) => {
    dispatch(actions.updateQuestion(item));
  };

  const handleOnDelete = async (index: number) => {
    const response_count = state[index]!.response_count as number;

    const shouldDeleteQuestion =
      response_count === 0 ||
      (response_count > 0 &&
        (await confirmAsync(
          <>
            Are you sure you want to delete this question?
            <label className='font-normal text-base'>
              You will lose all received responses to this question if you
              delete this question.
            </label>
          </>
        )));

    if (shouldDeleteQuestion) {
      dispatch(actions.deleteQuestion(index));

      // Scroll to the previous question
      onFocus?.(index - 1);
    }
  };

  useEffect(
    () =>
      update({
        ...testimonial,
        questions: state?.map((question) => ({
          uuid: question.uuid,
          bg_color: question.bg_color,
          question_rich: parseRichTextToSlateData(question.value as RichText),
          response_count: question.response_count,
          response_types: question.response_types,
        })),
      }),
    [state]
  );

  return (
    <>
      <p className='text-[21px] text-white font-bold p-0 mb-5'>Questions</p>
      <p className='text-base v2-text-white mb-5'>
        Ask a single question, or a series of questions up to {MAX_QUESTIONS}.
      </p>
      <Questionnaire
        data={state}
        maxQuestions={MAX_QUESTIONS}
        onAdd={handleOnAdd}
        inputType={QuestionnaireInputType.RichText}
        onUpdate={handleOnUpdate}
        onDelete={handleOnDelete}
        onFocus={onFocus}
        labelClassName='!v2-text-base'
      />
    </>
  );
}
